import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { GeneralResMock } from '../types/general.type'
import { LaboratoryResMock } from '../types/laboratory.type'

type Props = Partial<{
  page: number
  limit: number
  query: string
  sortDir: 'asc' | 'desc'
  id: string
  default: boolean
  /** `unpriced=true`: facility tests created from the catalogue that admin has not priced yet. */
  unpriced: boolean
  sortBy: string
}>

export const useLaboratory = (arg?: Props) => {
  const page = arg?.page || 1
  const limit = arg?.limit || 10
  const query = arg?.query
  const sortDir = arg?.sortDir || 'asc'
  const id = arg?.id
  const defaultVal = arg?.default
  const unpriced = arg?.unpriced
  const sortBy = arg?.sortBy || 'created_at'

  const { data, isLoading: laboratoryIsLoading } = useQuery({
    queryFn: async () => {
      let endpoint = unpriced ? `/lab_tests?unpriced=true&` : `/lab_tests?default=${defaultVal}&`
      if (page) endpoint = `${endpoint}page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}`
      if (query) endpoint = `${endpoint}&query=${query}`
      if (sortDir) endpoint = `${endpoint}&sort_dir=${sortDir}`
      if (sortBy) endpoint = `${endpoint}&sort_by=${sortBy}`
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<LaboratoryResMock[]>>(endpoint)
      return data
    },
    queryKey: ['lab_test', { defaultVal, limit, page, query, sortDir, sortBy, unpriced }]
  })

  const { data: singleLaboratory } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<LaboratoryResMock>>(`/lab_tests/${id}`)
      return data.data
    },
    queryKey: ['lab_test', id]
  })

  const { mutateAsync: createLaboratory } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post(`lab_tests`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab_test'] })
    }
  })

  const { mutateAsync: updateLaboratory } = useMutation({
    mutationFn: async ({ data, id }: { data: any; id: string }) => {
      const { data: updatedData } = await AXIOS_INSTANCE.patch(`lab_tests/${id}`, data, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return updatedData
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab_test'] })
    }
  })

  const { mutateAsync: deleteLaboratory } = useMutation({
    mutationFn: async (id: string) => {
      await AXIOS_INSTANCE.delete(`lab_tests/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab_test'] })
    }
  })

  return {
    createLaboratory,
    deleteLaboratory,
    labPagination: data?.meta.pagination,
    laboratories: data?.data,
    laboratoryIsLoading,
    singleLaboratory,
    updateLaboratory
  }
}
